/**
 * Topbar Panel
 *
 * Topbar view - handles the topbar and some generic popups.
 *
 * Also handles global drag-and-drop support.
 *
 * @author Guangcong Luo <guangcongluo@gmail.com>
 * @license AGPLv3
 */

import preact from "../js/lib/preact";
import {
	Config, NARROW_MODE_HEADER_WIDTH, PS, type PSRoom, type RoomID, VERTICAL_HEADER_WIDTH,
} from "./client-main";
import { PSPanelErrorBoundary, PSView } from "./panels";
import type { Battle } from "./battle";
import { Dex } from "./battle-dex";
import { BattleLog } from "./battle-log"; // optional

/** @podown(layout) news + DM mini-windows docked above vertical nav tabs */
class MiniWindowStack extends preact.Component {
	handleDragStart = (e: DragEvent) => {
		const room = PS.getRoom(e.currentTarget);
		if (!room) return;
		const foreground = (PS.leftPanel.id === room.id || PS.rightPanel?.id === room.id);
		PS.dragging = { type: 'room', roomid: room.id, foreground };
	};
	handleDragEnter = (e: DragEvent) => {
		e.preventDefault();
		if (PS.dragging?.type !== 'room') return;
		const draggingRoom = PS.dragging.roomid;
		if (draggingRoom === null) return;

		const draggedOverRoom = PS.getRoom(e.target);
		if (draggingRoom === draggedOverRoom?.id) return;

		const index = PS.miniRoomList.indexOf(draggedOverRoom?.id as any);
		if (index >= 0) {
			PS.dragOnto(PS.rooms[draggingRoom]!, 'mini-window', index);
		} else if (PS.rooms[draggingRoom]?.location !== 'mini-window') {
			PS.dragOnto(PS.rooms[draggingRoom]!, 'mini-window', 0);
		}
	};
	/** @podown(layout) any click in the stack — block drawer close from scrollToRoom */
	handleStackClickCapture = () => {
		PodownNavHint.holdNavOpen();
	};
	handleClickMinimize = (e: MouseEvent) => {
		if ((e.target as Element)?.getAttribute('data-cmd')) {
			return;
		}
		if (((e.target as Element)?.parentNode as Element)?.getAttribute('data-cmd')) {
			return;
		}
		const room = PS.getRoom(e.currentTarget);
		if (room) {
			room.minimized = !room.minimized;
			this.forceUpdate();
			PodownNavHint.keepNavOpen();
		}
	};
	handleCloseClick = () => {
		// data-cmd /close still handled by PSView; keep drawer after leave
		PodownNavHint.holdNavOpen();
		requestAnimationFrame(() => PodownNavHint.keepNavOpen());
	};
	override render() {
		return <div
			class="sidebar-mini-windows"
			onDragEnter={this.handleDragEnter}
			onClickCapture={this.handleStackClickCapture}
		>
			{PS.miniRoomList.map(roomid => {
				const room = PS.rooms[roomid];
				if (!room) return null;
				const isNews = room.id === 'news';
				const notifying = room.notifications.length ? ' notifying' :
					room.isSubtleNotifying ? ' subtle-notifying' : '';
				return <div
					class={`mini-window${room.minimized ? ' collapsed' : ''}${room === PS.room ? ' focused' : ''}`}
					key={roomid} data-roomid={roomid}
				>
					<h3
						class={`mini-window-header${notifying}`} draggable
						onDragStart={this.handleDragStart} onClick={this.handleClickMinimize}
					>
						{/* @podown(layout) no maximize in sidebar; news also has no close */}
						{!isNews && <button
							class="closebutton" data-cmd="/close" aria-label="Close" tabIndex={-1}
							onClick={this.handleCloseClick}
						>
							<i class="fa fa-times-circle" aria-hidden></i>
						</button>}
						<button class="minimizebutton" tabIndex={-1} aria-label="Expand/Collapse">
							<i class="fa fa-minus-circle" aria-hidden></i>
						</button>
						{room.title}
					</h3>
					<PSPanelErrorBoundary key={room.id} room={room} />
				</div>;
			})}
		</div>;
	}
}

window.addEventListener('dragover', e => {
	// this prevents the bounce-back animation
	e.preventDefault();
});

/** @podown(layout) mobile nav: open once on connect + first-visit gesture hint (cookie) */
const PODOWN_NAV_HINT_COOKIE = 'podown_nav_hint';
const PODOWN_NAV_HINT_MAX_AGE = 60 * 60 * 24 * 400; // ~400d

var PodownNavHint = {
	openedOnConnect: false,
	hintRunning: false,
	hintDone: false,
	retryCount: 0,
	scrollPatched: false,
	/** @podown(layout) briefly suppress scrollToRoom after sidebar mini-window UI */
	holdingNavOpen: false,
	holdNavOpenTimer: null as ReturnType<typeof setTimeout> | null,
	origScrollToRoom: null as null | (() => void),
	timers: [] as ReturnType<typeof setTimeout>[],
	clearTimers() {
		for (const t of this.timers) clearTimeout(t);
		this.timers = [];
	},
	forceHint() {
		// set early by podown-i18n.js before PS router strips ?/# 
		return !!(window as any).__podownNavHintForce;
	},
	hasCookie() {
		return document.cookie.split(';').some(c => c.trim().startsWith(`${PODOWN_NAV_HINT_COOKIE}=`));
	},
	setCookie() {
		document.cookie = `${PODOWN_NAV_HINT_COOKIE}=1; max-age=${PODOWN_NAV_HINT_MAX_AGE}; path=/; SameSite=Lax`;
	},
	clearForce() {
		try { sessionStorage.removeItem('podown_nav_hint_force'); } catch { /* ignore */ }
		(window as any).__podownNavHintForce = false;
	},
	/** true when the drawer (swipe) layout is active */
	isNarrow() {
		if (PSView.narrowMode) return true;
		if (typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 700px)').matches) {
			return true;
		}
		return document.documentElement.clientWidth <= 700;
	},
	i18nReady() {
		return !document.documentElement.classList.contains('podown-i18n-pending');
	},
	ensureNarrowMode() {
		if (PSView.narrowMode) return true;
		if (!this.isNarrow()) return false;
		// layout may lag behind viewport — sync so scroll snap / drawer works
		PSView.narrowMode = true;
		PSView.verticalHeaderWidth = NARROW_MODE_HEADER_WIDTH;
		PSView.updateScrollSnap();
		return true;
	},
	/** Instant pin — no animateSnap / no delayed jump after first paint */
	pinNavOpenInstant() {
		if (!this.ensureNarrowMode()) return false;
		PSView.clearSnap();
		PSView.snapRestingX = 0;
		// CSS scroll-snap can animate; peel it off for the pin
		document.documentElement.classList.remove('scroll-snap-enabled');
		PSView.setScrollX(0);
		if (PSView.scrollFrame) PSView.scrollFrame.scrollLeft = 0;
		try { window.scrollTo(0, 0); } catch { /* ignore */ }
		return true;
	},
	/** @podown(layout) keep mobile drawer open (sidebar mini-window interactions) */
	keepNavOpen() {
		if (!this.isNarrow()) return;
		this.pinNavOpenInstant();
	},
	/** Suppress the next scrollToRoom(s) briefly, and pin drawer open now */
	holdNavOpen() {
		this.holdingNavOpen = true;
		this.keepNavOpen();
		if (this.holdNavOpenTimer) clearTimeout(this.holdNavOpenTimer);
		this.holdNavOpenTimer = setTimeout(() => {
			this.holdingNavOpen = false;
			this.holdNavOpenTimer = null;
		}, 400);
	},
	/** While booting / mini-window focus — don't yank the drawer shut */
	patchScrollToRoom() {
		if (this.scrollPatched) return;
		this.scrollPatched = true;
		this.origScrollToRoom = PSView.scrollToRoom.bind(PSView);
		PSView.scrollToRoom = () => {
			if (!this.openedOnConnect && this.isNarrow()) {
				this.pinNavOpenInstant();
				return;
			}
			// @podown(layout) sidebar mini-window click/focus must not close the drawer
			if (this.isNarrow() && (this.holdingNavOpen || PS.room?.location === 'mini-window')) {
				this.keepNavOpen();
				return;
			}
			this.origScrollToRoom!();
		};
	},
	scheduleRetry() {
		if (this.retryCount >= 40) return; // ~4s
		this.retryCount++;
		this.timers.push(setTimeout(() => this.openNavOnceOnConnect(), 100));
	},
	openNavOnceOnConnect() {
		this.patchScrollToRoom();
		// Pin nav immediately — do NOT wait for i18n (that caused the refresh jank)
		if (!this.ensureNarrowMode()) {
			this.scheduleRetry();
			return;
		}
		this.openedOnConnect = true;
		this.pinNavOpenInstant();
		// onboarding overlay still waits for chrome to be visible
		if (!this.i18nReady()) {
			this.scheduleRetry();
			return;
		}
		this.maybeStartHint();
	},
	maybeStartHint() {
		if (this.hintRunning || this.hintDone) return;
		if (this.hasCookie() && !this.forceHint()) return;
		if (!this.i18nReady() || !this.ensureNarrowMode()) {
			this.scheduleRetry();
			return;
		}
		this.hintRunning = true;
		this.retryCount = 0;
		this.pinNavOpenInstant();
		this.mount();
		this.runSequence();
	},
	mount() {
		if (document.getElementById('podown-nav-hint')) return;
		const root = document.createElement('div');
		root.id = 'podown-nav-hint';
		root.className = 'podown-nav-hint';
		root.setAttribute('data-step', '1');
		root.setAttribute('role', 'dialog');
		root.setAttribute('aria-label', '내비게이션 안내');
		root.innerHTML = [
			'<div class="podown-nav-hint-scrim"></div>',
			'<div class="podown-nav-hint-spot" aria-hidden="true"></div>',
			'<div class="podown-nav-hint-finger" aria-hidden="true">👆</div>',
			'<p class="podown-nav-hint-caption"></p>',
			'<button type="button" class="podown-nav-hint-skip">닫기</button>',
		].join('');
		document.body.appendChild(root);
		root.querySelector('.podown-nav-hint-skip')!.addEventListener('click', e => {
			e.stopPropagation();
			this.finish();
		});
		root.addEventListener('click', () => this.advanceFromUser());
	},
	setStep(step: 1 | 2, caption: string) {
		const root = document.getElementById('podown-nav-hint');
		if (!root) return;
		root.setAttribute('data-step', String(step));
		const cap = root.querySelector('.podown-nav-hint-caption');
		if (cap) cap.textContent = caption;
	},
	runSequence() {
		this.clearTimers();
		// Step 1: nav open — press toward the chat room (right)
		PSView.clearSnap();
		PSView.snapRestingX = 0;
		PSView.setScrollX(0);
		this.setStep(1, '채팅방 쪽을 눌러 들어가세요');
		this.timers.push(setTimeout(() => this.goStep2(), 2800));
	},
	goStep2() {
		if (!this.hintRunning) return;
		PSView.clearSnap();
		PSView.snapRestingX = NARROW_MODE_HEADER_WIDTH;
		PSView.setScrollX(NARROW_MODE_HEADER_WIDTH);
		this.setStep(2, '밀어서 메뉴를 열 수 있어요');
		this.timers.push(setTimeout(() => {
			if (!this.hintRunning) return;
			PSView.clearSnap();
			PSView.animateSnap(0, 280);
			PSView.snapRestingX = 0;
			this.timers.push(setTimeout(() => this.finish(), 900));
		}, 2800));
	},
	advanceFromUser() {
		const root = document.getElementById('podown-nav-hint');
		if (!root || !this.hintRunning) return;
		const step = root.getAttribute('data-step');
		this.clearTimers();
		if (step === '1') {
			this.goStep2();
			return;
		}
		PSView.clearSnap();
		PSView.snapRestingX = 0;
		PSView.scrollToHeader();
		this.finish();
	},
	finish() {
		this.clearTimers();
		this.hintRunning = false;
		this.hintDone = true;
		this.setCookie();
		this.clearForce();
		document.getElementById('podown-nav-hint')?.remove();
		if (this.isNarrow()) {
			this.ensureNarrowMode();
			PSView.clearSnap();
			PSView.snapRestingX = 0;
			PSView.setScrollX(0);
		}
	},
};
// expose for MainMenuPanel (script load order / babel globals)
(window as any).PodownNavHint = PodownNavHint;
// @podown(layout) prefer nav-open resting before first paint/settle
if (typeof PSView !== 'undefined' && PodownNavHint.isNarrow()) {
	PSView.snapRestingX = 0;
	PodownNavHint.patchScrollToRoom();
}

/** @podown(layout) EV/range (and Stats dialog) must not open the mobile nav drawer */
function podownBlocksNavSnap(target: EventTarget | null): boolean {
	if ((PSView as any).__podownBlockSnap) return true;
	let el = target instanceof Element ? target : null;
	while (el) {
		if (el instanceof HTMLInputElement && el.type === 'range') return true;
		if (el.classList?.contains('evslider')) return true;
		if (el.getAttribute('role') === 'dialog' && el.getAttribute('aria-label') === 'Stats') return true;
		el = el.parentElement;
	}
	return false;
}
(function patchNavSnapForRangeControls() {
	if ((PSView as any).__podownNavSnapRangePatched) return;
	(PSView as any).__podownNavSnapRangePatched = true;
	const origStart = PSView.startSnapGesture.bind(PSView);
	const origMove = PSView.moveSnapGesture.bind(PSView);
	const origFinish = PSView.finishSnapGesture.bind(PSView);
	PSView.startSnapGesture = (x: number, y: number, target: EventTarget | null) => {
		if (podownBlocksNavSnap(target)) {
			PSView.clearSnap();
			PSView.snapStart = null;
			return;
		}
		return origStart(x, y, target);
	};
	PSView.moveSnapGesture = (x: number, y: number) => {
		if ((PSView as any).__podownBlockSnap || (PSView.snapStart && podownBlocksNavSnap(PSView.snapStart.target))) {
			PSView.clearSnap();
			PSView.snapStart = null;
			return false;
		}
		return origMove(x, y);
	};
	PSView.finishSnapGesture = (x: number, y: number) => {
		if ((PSView as any).__podownBlockSnap || (PSView.snapStart && podownBlocksNavSnap(PSView.snapStart.target))) {
			PSView.clearSnap();
			PSView.snapStart = null;
			return;
		}
		return origFinish(x, y);
	};
})();

/** @podown(layout) Stats/EV form sets this while a touch/pen gesture is active inside it */
(PSView as any).holdNavSnapBlock = (hold: boolean) => {
	(PSView as any).__podownBlockSnap = hold;
	if (hold) {
		PSView.clearSnap();
		PSView.snapStart = null;
	}
};

export class PSHeader extends preact.Component {
	faviconNotifying = false;
	updateFavicon = () => {
		const notifying = Object.values(PS.rooms).some(room => room?.notifications.length);
		if (notifying === this.faviconNotifying) return;

		const favicon = document.querySelector<HTMLLinkElement>('#dynamic-favicon');
		if (!favicon) return;
		favicon.href = `${Dex.resourcePrefix}${notifying ? 'favicon-notify.ico' : 'favicon.ico'}`;
		this.faviconNotifying = notifying;
	};
	static toggleMute = (e: Event) => {
		PS.prefs.set('mute', !PS.prefs.mute);
		PS.update();
	};
	static handleDragEnter = (e: DragEvent) => {
		// console.log('dragenter ' + e.dataTransfer!.dropEffect);
		e.preventDefault();
		if (PS.dragging?.type !== 'room') return;
		/** the element being passed over */
		const target = e.currentTarget as HTMLAnchorElement;

		const draggingRoom = PS.dragging.roomid;
		if (draggingRoom === null) return;

		const draggedOverRoom = PS.router.extractRoomID(target.href);
		if (draggedOverRoom === null) return; // should never happen
		if (draggingRoom === draggedOverRoom) return;

		const leftIndex = PS.leftRoomList.indexOf(draggedOverRoom);
		if (leftIndex >= 0) {
			PS.dragOnto(PS.rooms[draggingRoom]!, 'left', leftIndex);
		} else {
			const rightIndex = PS.rightRoomList.indexOf(draggedOverRoom);
			if (rightIndex >= 0) {
				PS.dragOnto(PS.rooms[draggingRoom]!, 'right', rightIndex);
			} else {
				// eslint-disable-next-line no-useless-return
				return;
			}
		}

		// dropEffect !== 'none' prevents bounce-back animation in
		// Chrome/Safari/Opera
		// if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
	};
	static handleDragStart = (e: DragEvent) => {
		const roomid = PS.router.extractRoomID((e.currentTarget as HTMLAnchorElement).href);
		if (!roomid) return; // should never happen

		PS.dragging = { type: 'room', roomid };
	};
	static roomInfo(room: PSRoom) {
		const RoomType = PS.roomTypes[room.type];
		let icon = RoomType?.icon || <i class="fa fa-file-text-o" aria-hidden></i>;
		let title = room.title;
		switch (room.type) {
		case 'battle':
			const idChunks = room.id.split('-');
			let formatName;
			// TODO: relocate to room implementation
			if (idChunks.length <= 2) {
				if (idChunks[1] === 'uploadedreplay') formatName = 'Uploaded Replay';
			} else {
				formatName = window.BattleLog ? BattleLog.formatName(idChunks[1]) : idChunks[1];
			}
			if (!title) {
				const battle = (room as any).battle as Battle | undefined;
				const p1 = battle?.p1?.name || '';
				const p2 = battle?.p2?.name || '';
				if (p1 && p2) {
					title = `${p1} v. ${p2}`;
				} else if (p1 || p2) {
					title = `${p1}${p2}`;
				} else {
					title = `(empty room)`;
				}
			}
			icon = <i class="text">{formatName}</i>;
			break;
		case 'html':
		default:
			if (title.startsWith('[')) {
				let closeBracketIndex = title.indexOf(']');
				if (closeBracketIndex > 0) {
					icon = <i class="text">{title.slice(1, closeBracketIndex)}</i>;
					title = title.slice(closeBracketIndex + 1);
					break;
				}
			}
			break;
		}
		return { icon, title };
	}
	static renderRoomTab(id: RoomID, noAria?: boolean, includeMiniNotifications = true) {
		const room = PS.rooms[id];
		if (!room) return null;
		const closable = (id === '' || id === 'rooms' ? '' : ' closable');
		const cur = PS.isVisiblePanel(room) ? ' cur' : '';
		let notifying = room.isSubtleNotifying ? ' subtle-notifying' : '';
		let hoverTitle = '';
		let notifications = room.notifications;
		if (id === '' && includeMiniNotifications) {
			for (const roomid of PS.miniRoomList) {
				const miniNotifications = PS.rooms[roomid]?.notifications;
				if (miniNotifications?.length) notifications = [...notifications, ...miniNotifications];
			}
		}
		if (notifications.length) {
			notifying = ' notifying';
			for (const notif of notifications) {
				if (!notif.body) continue;
				hoverTitle += `${notif.title}\n${notif.body}\n`;
			}
		}
		let className = `roomtab button${notifying}${closable}${cur}`;

		let { icon, title: roomTitle } = PSHeader.roomInfo(room);
		if (room.type === 'rooms' && PS.leftPanelWidth !== null) roomTitle = '';
		if (room.type === 'battle') className += ' roomtab-battle';

		let closeButton = null;
		if (closable) {
			closeButton = <button class="closebutton" name="closeRoom" value={id} aria-label="Close">
				<i class="fa fa-times-circle" aria-hidden></i>
			</button>;
		}
		const aria: Record<string, string> = noAria ? {} : {
			"role": "tab", "id": `roomtab-${id}`, "aria-selected": cur ? "true" : "false",
		};
		if (id === 'rooms') aria['aria-label'] = "Join chat";
		return <li class={id === '' ? 'home-li' : ''} key={id}>
			<a
				class={className} href={`/${id}`} draggable={true} title={hoverTitle || undefined}
				onDragEnter={this.handleDragEnter} onDragStart={this.handleDragStart}
				{...aria}
			>
				{icon} {roomTitle}
			</a>
			{closeButton}
		</li>;
	}
	static notifyingMiniRoomTabs() {
		return PS.miniRoomList.filter(roomid => PS.rooms[roomid]?.notifications.length);
	}
	handleResize = () => {
		if (!this.base) return;

		if (PS.leftPanelWidth === null) {
			const width = document.documentElement.clientWidth;
			const oldNarrowMode = PSView.narrowMode;
			PSView.narrowMode = width <= 700;
			PSView.verticalHeaderWidth = PSView.narrowMode ? NARROW_MODE_HEADER_WIDTH : VERTICAL_HEADER_WIDTH;
			document.documentElement.style.width = PSView.useCSSScrollSnap() ?
				`${width + NARROW_MODE_HEADER_WIDTH}px` : 'auto';
			if (oldNarrowMode !== PSView.narrowMode) {
				PSView.updateScrollSnap();
				// @podown(layout) pin nav open BEFORE re-render so refresh doesn't animate shut→open
				if (PSView.narrowMode) {
					PodownNavHint.pinNavOpenInstant();
				}
				PS.update();
				if (PSView.narrowMode) {
					PodownNavHint.openNavOnceOnConnect();
				}
			} else if (PSView.narrowMode && !PodownNavHint.openedOnConnect) {
				PodownNavHint.pinNavOpenInstant();
				PodownNavHint.openNavOnceOnConnect();
			}
			return;
		}
		if (PSView.narrowMode) {
			document.documentElement.style.width = 'auto';
			PSView.narrowMode = false;
			PSView.updateScrollSnap();
		}

		let userbarLeft = this.base.querySelector('div.userbar .icon.button')?.getBoundingClientRect()?.left;
		if (userbarLeft) userbarLeft -= 5;
		const plusTabRight = this.base.querySelector('a.roomtab[aria-label="Join chat"]')?.getBoundingClientRect()?.right;
		const overflow = this.base.querySelector<HTMLElement>('.overflow');

		if (!overflow || !userbarLeft || !plusTabRight) return;

		const maintabbar = this.base.querySelector<HTMLElement>('div.maintabbar');
		if (maintabbar) {
			const userbarRight = window.innerWidth - userbarLeft;
			maintabbar.style.marginRight = `${userbarRight}px`;
			overflow.style.right = `${userbarRight}px`;
		}

		if (plusTabRight > userbarLeft - 3) {
			overflow.style.display = 'block';
		} else {
			overflow.style.display = 'none';
		}
	};
	override componentDidMount() {
		PS.user.subscribe(() => {
			this.forceUpdate();
		});
		window.addEventListener('resize', this.handleResize);
		this.handleResize();
		this.updateFavicon();
		// @podown(layout) pin sync now; re-pin next frame after PSView attaches scrollFrame
		PodownNavHint.openNavOnceOnConnect();
		requestAnimationFrame(() => {
			PodownNavHint.pinNavOpenInstant();
			PodownNavHint.openNavOnceOnConnect();
		});
	}
	override componentDidUpdate() {
		this.handleResize();
		this.updateFavicon();
	}
	renderUser() {
		if (!PS.connection?.connected) {
			// @podown(layout) reconnect affordance (Home panel removed)
			return <span>
				<button class="button" disabled><em>Offline</em></button>
				{PS.isOffline && <>
					{' '}
					<button class="button" data-cmd="/reconnect">
						<i class="fa fa-plug" aria-hidden></i> Reconnect
					</button>
				</>}
			</span>;
		}
		if (PS.user.initializing) {
			return <button class="button" disabled><em>Connecting...</em></button>;
		}
		if (!PS.user.named) {
			// @podown(login-local) guests open login form
			return <a class="button" href="login">로그인</a>;
		}
		const userColor = window.BattleLog && `color:${PS.user.away ? '#888' : BattleLog.usernameColor(PS.user.userid)}`;
		return <span class="username" style={userColor}>
			<span class="usernametext">{PS.user.name}</span>
		</span>;
	}
	renderVertical() {
		return <div
			id="header" class="header-vertical" role="navigation"
			style={`width:${PSView.verticalHeaderWidth - 7}px`} onClick={PSView.scrollToHeader}
		>
			<div class="maintabbarbottom"></div>
			<div class="scrollable-part">
				{/* @podown(brand) sidebar logo — transparent wordmark */}
				<img
					class="logo"
					src="/fx/podown-logo.png"
					alt="포다운"
					width="108"
					height="52"
				/>
				{/* @podown(layout) news + DM stack above nav tabs */}
				<MiniWindowStack />
				<div class="tablist" role="tablist">
					{/* @podown(layout) skip Home tab (leftRoomList[0]); stack replaces notifying mini tabs */}
					<ul>
						{PS.leftRoomList.slice(1).map(roomid => PSHeader.renderRoomTab(roomid))}
					</ul>
					{/* @podown(layout) hide Rooms panel — lobby-only */}
					<ul class="siderooms">
						{PS.rightRoomList.filter(roomid => roomid !== 'rooms').map(roomid => PSHeader.renderRoomTab(roomid))}
					</ul>
				</div>
			</div>
			<div class="userbar">
				{this.renderUser()} {}
				<div style="float:right">
					<button class="icon button" data-href="volume" title="Sound" aria-label="Sound" onDblClick={PSHeader.toggleMute}>
						<i class={PS.prefs.mute ? 'fa fa-volume-off' : 'fa fa-volume-up'}></i>
					</button> {}
					<button class="icon button" data-href="options" title="Options" aria-label="Options">
						<i class="fa fa-cog" aria-hidden></i>
					</button>
				</div>
			</div>
			{null /* maintabbar */}
			{null /* overflow */}
		</div>;
	}
	override render() {
		if (PS.leftPanelWidth === null) {
			return this.renderVertical();
		}
		return <div id="header" class="header" role="navigation">
			<div class="maintabbarbottom"></div>
			{null /* vertical tabs */}
			<div class="userbar">
				{this.renderUser()} {}
				<button class="icon button" data-href="volume" title="Sound" aria-label="Sound" onDblClick={PSHeader.toggleMute}>
					<i class={PS.prefs.mute ? 'fa fa-volume-off' : 'fa fa-volume-up'}></i>
				</button> {}
				<button class="icon button" data-href="options" title="Options" aria-label="Options">
					<i class="fa fa-cog" aria-hidden></i>
				</button>
			</div>
			<div class="tabbar maintabbar"><div class="inner-1" role={PS.leftPanelWidth ? 'none' : 'tablist'}><div class="inner-2">
				<ul class="maintabbar-left" style={{ width: `${PS.leftPanelWidth}px` }} role={PS.leftPanelWidth ? 'tablist' : 'none'}>
					<li>
						<img
							class="logo"
							src="/fx/podown-logo.png"
							alt="포다운"
							width="108"
							height="52"
						/>
					</li>
					{/* @podown(layout) skip Home tab */}
					{PS.leftRoomList.slice(1).map(roomid => PSHeader.renderRoomTab(roomid))}
				</ul>
				<ul class="maintabbar-right" role={PS.leftPanelWidth ? 'tablist' : 'none'}>
					{/* @podown(layout) hide Rooms panel — lobby-only */}
					{PS.rightRoomList.filter(roomid => roomid !== 'rooms').map(roomid => PSHeader.renderRoomTab(roomid))}
				</ul>
			</div></div></div>
			<div class="overflow">
				<button name="tablist" class="button" data-href="roomtablist" aria-label="All tabs" type="button">
					<i class="fa fa-caret-down" aria-hidden></i>
				</button>
			</div>
		</div>;
	}
}

export class PSMiniHeader extends preact.Component {
	menuOpen?: boolean;
	override componentDidMount() {
		PSView.addScrollListener(this.handleScroll);
	}
	override componentWillUnmount() {
		PSView.removeScrollListener(this.handleScroll);
	}
	handleScroll = () => {
		if (this.menuOpen !== !PSView.getScrollX()) this.forceUpdate();
	};
	override render() {
		this.menuOpen = !PSView.getScrollX();

		if (PS.leftPanelWidth !== null) return null;

		const notificationsCount = Object.values(PS.rooms).filter(
			room => room !== PS.room && room?.notifications.length
		).length;
		const { icon, title } = PSHeader.roomInfo(PS.panel);
		const userColor = window.BattleLog && `color:${PS.user.away ? '#888' : BattleLog.usernameColor(PS.user.userid)}`;
		const showMenuButton = PSView.narrowMode;
		const notifying = (
			!showMenuButton && this.menuOpen && Object.values(PS.rooms).some(room => room!.notifications.length)
		) ? ' notifying' : '';
		const menuButton = !showMenuButton ? (
			null
		) : !this.menuOpen ? (
			<button onClick={PSView.scrollToHeader} class={`mini-header-left ${notifying}`} aria-label="Menu">
				{!!notificationsCount && <div class="notification-badge">{notificationsCount}</div>}
				<i class="fa fa-bars" aria-hidden></i>
			</button>
		) : (
			<button onClick={PSView.scrollToRoom} class="mini-header-left" aria-label="Menu">
				<i class="fa fa-arrow-right" aria-hidden></i>
			</button>
		);
		return <div class="mini-header" style={`left:${PSView.verticalHeaderWidth + (PSView.narrowMode ? 0 : -1)}px;`}>
			{menuButton}
			{icon} {title}
			<button data-href="options" class="mini-header-right" aria-label="Options">
				{PS.user.named ? <strong style={userColor}>{PS.user.name}</strong> : <i class="fa fa-cog" aria-hidden></i>}
			</button>
		</div>;
	}
}

preact.render(<PSView />, document.body, document.getElementById('ps-frame')!);
